/**
 * MaintenanceTab — equipment register. Each equipment holds multiple
 * maintenance plans (own interval + steps + photos + history). A timeline at
 * the top shows which machine is due on which day.
 */
import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Wrench, Plus, Trash2, Pencil, CheckCircle2, Loader2, ImagePlus, X, History, ChevronDown, ChevronUp } from "lucide-react";
import WaterQualityCard from "./WaterQualityCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const imgUrl = (id) => (id ? `${API}/maintenance/images/${id}` : null);

async function resizeAndUpload(file) {
  const dataUrl = await new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const max = 1200;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.8));
    };
    img.onerror = reject;
    img.src = url;
  });
  const base64 = dataUrl.split(",")[1];
  const res = await axios.post(`${API}/maintenance/images`, { data: base64, content_type: "image/jpeg" });
  return res.data.id;
}

function ImagePicker({ imageId, onChange, testId }) {
  const [busy, setBusy] = useState(false);
  const pick = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async () => {
      const f = input.files?.[0];
      if (!f) return;
      setBusy(true);
      try {
        onChange(await resizeAndUpload(f));
      } catch {
        toast.error("Image upload failed");
      } finally {
        setBusy(false);
      }
    };
    input.click();
  };
  return (
    <div className="flex items-center gap-2">
      {imageId ? (
        <div className="relative">
          <img src={imgUrl(imageId)} alt="" className="w-14 h-14 object-cover rounded-md border" />
          <button type="button" onClick={() => onChange(null)}
            className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full p-0.5"
            data-testid={testId ? `${testId}-remove` : undefined}>
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <Button type="button" variant="outline" size="sm" onClick={pick} disabled={busy} data-testid={testId}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ImagePlus className="w-4 h-4 mr-1" />Photo</>}
        </Button>
      )}
    </div>
  );
}

const EMPTY_PLAN = () => ({ id: null, title: "", interval_days: 30, start_date: "", steps: [] });
const EMPTY_FORM = () => ({ name: "", location: "", notes: "", image_id: null, plans: [EMPTY_PLAN()] });

const DAY_MS = 86400000;
const CELL_W = 42;
const PAST_DAYS = 21;
const FUTURE_DAYS = 90;
const localKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function buildDays() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = [];
  for (let i = -PAST_DAYS; i <= FUTURE_DAYS; i++) {
    const d = new Date(today.getTime() + i * DAY_MS);
    days.push({ date: d, key: localKey(d), isToday: i === 0, isPast: i < 0 });
  }
  return days;
}

// Maps for one plan: dateKey -> status, and dateKey -> completion record.
function planOccurrences(plan, todayKey) {
  const map = {};
  const recs = {};
  (plan.history || []).forEach((h) => {
    const k = h.due || (h.at ? localKey(new Date(h.at)) : null);
    if (k) { map[k] = "done"; recs[k] = h; }
  });
  if (plan.next_due && plan.interval_days) {
    let d = new Date(`${plan.next_due}T00:00:00`);
    const end = new Date(Date.now() + FUTURE_DAYS * DAY_MS);
    let guard = 0;
    while (d <= end && guard < 400) {
      const k = localKey(d);
      if (!map[k]) map[k] = k < todayKey ? "overdue" : "pending";
      d = new Date(d.getTime() + plan.interval_days * DAY_MS);
      guard++;
    }
  }
  return { map, recs };
}

function EquipmentTimeline({ equipment, days, todayKey, onSelect, onSelectDone }) {
  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollLeft = Math.max(0, (PAST_DAYS - 3) * CELL_W);
  }, []);
  const plans = equipment.plans || [];
  if (plans.length === 0) return <p className="text-xs text-muted-foreground px-1 pt-1">No plans — edit to add.</p>;
  const occ = plans.map((p) => planOccurrences(p, todayKey));
  return (
    <div className="flex mt-2 border rounded-lg overflow-hidden" data-testid={`equip-timeline-${equipment.id}`}>
      {/* Fixed plan labels */}
      <div className="shrink-0 w-[150px] sm:w-[190px] border-r bg-neutral-50/70">
        <div className="h-9 border-b flex items-end px-2 pb-1">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400">Plan</span>
        </div>
        {plans.map((p) => (
          <button key={p.id} onClick={() => onSelect(p)} data-testid={`plan-label-${p.id}`}
            className="h-9 w-full border-b last:border-b-0 flex flex-col justify-center px-2 text-left hover:bg-neutral-100">
            <span className="text-xs font-semibold truncate leading-tight">{p.title}</span>
            <span className="text-[10px] text-muted-foreground leading-tight">every {p.interval_days}d{p.overdue ? <span className="text-red-600 font-bold"> · overdue</span> : ""}</span>
          </button>
        ))}
      </div>
      {/* Scrollable calendar */}
      <div ref={scrollRef} className="overflow-x-auto flex-1" data-testid={`equip-timeline-scroll-${equipment.id}`}>
        <div style={{ width: days.length * CELL_W }}>
          <div className="flex h-9 border-b">
            {days.map((d) => (
              <div key={d.key} style={{ width: CELL_W }}
                className={`shrink-0 flex flex-col items-center justify-center border-r last:border-r-0 ${d.isToday ? "bg-sky-100" : d.isPast ? "bg-neutral-50" : "bg-white"}`}>
                <span className={`text-[9px] leading-none ${d.isToday ? "text-sky-700 font-bold" : "text-neutral-400"}`}>
                  {d.date.toLocaleDateString("en-AU", { month: "short" })}
                </span>
                <span className={`text-[11px] leading-tight font-semibold ${d.isToday ? "text-sky-700" : "text-neutral-600"}`}>
                  {d.date.getDate()}
                </span>
              </div>
            ))}
          </div>
          {plans.map((p, pi) => (
            <div key={p.id} className="flex h-9 border-b last:border-b-0">
              {days.map((d) => {
                const st = occ[pi].map[d.key];
                const rec = occ[pi].recs[d.key];
                return (
                  <div key={d.key} style={{ width: CELL_W }}
                    className={`shrink-0 border-r last:border-r-0 flex items-center justify-center ${d.isToday ? "bg-sky-50" : d.isPast ? "bg-neutral-50/60" : ""}`}>
                    {st && (
                      <button
                        onClick={() => (st === "done" ? onSelectDone(p, rec) : onSelect(p))}
                        title={`${p.title} · ${d.key} · ${st === "done" ? `completed${rec?.note ? ` — ${rec.note}` : ""}` : st}`}
                        data-testid={`tl-cell-${equipment.id}-${p.id}-${d.key}`}
                        className={`w-7 h-6 rounded-md transition-transform hover:scale-110 ${
                          st === "done" ? "bg-emerald-500 hover:bg-emerald-600"
                          : st === "overdue" ? "bg-amber-400 ring-2 ring-red-500 hover:bg-amber-500"
                          : "bg-amber-400 hover:bg-amber-500"
                        }`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function MaintenanceTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM());
  const [service, setService] = useState(null); // { equipment, plan }
  const [doneNote, setDoneNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [completedOpen, setCompletedOpen] = useState(false);
  const [doneInfo, setDoneInfo] = useState(null); // { equipment, plan, rec }

  const load = async () => {
    try {
      const res = await axios.get(`${API}/maintenance/equipment`, { headers: { "x-no-cache": "1" } });
      setItems(res.data || []);
    } catch {
      toast.error("Failed to load equipment");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const days = useMemo(() => buildDays(), [items]);
  const todayKey = localKey(new Date());

  // All past completions (for the collapsed "Completed" section at the bottom).
  const completedLog = items
    .flatMap((e) => (e.plans || []).flatMap((p) => (p.history || []).map((h) => ({ equipment: e, plan: p, ...h }))))
    .sort((a, b) => (b.at || "").localeCompare(a.at || ""));

  const openAdd = () => { setEditId(null); setForm(EMPTY_FORM()); setEditOpen(true); };
  const openEdit = (e) => {
    setEditId(e.id);
    setForm({
      name: e.name, location: e.location || "", notes: e.notes || "", image_id: e.image_id || null,
      plans: (e.plans || []).map((p) => ({ id: p.id, title: p.title, interval_days: p.interval_days, start_date: p.start_date || "", steps: (p.steps || []).map((s) => ({ ...s })) })),
    });
    setEditOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!form.plans.some((p) => p.title.trim())) { toast.error("Add at least one maintenance plan"); return; }
    setBusy(true);
    try {
      const payload = { ...form, plans: form.plans.filter((p) => p.title.trim()).map((p) => ({ ...p, interval_days: Number(p.interval_days) || 30 })) };
      if (editId) await axios.patch(`${API}/maintenance/equipment/${editId}`, payload);
      else await axios.post(`${API}/maintenance/equipment`, payload);
      toast.success("Saved");
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete "${e.name}" and all its maintenance history?`)) return;
    try {
      await axios.delete(`${API}/maintenance/equipment/${e.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const markDone = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/maintenance/equipment/${service.equipment.id}/plans/${service.plan.id}/complete`, { note: doneNote.trim() });
      toast.success("Maintenance recorded");
      const freshPlan = (res.data.plans || []).find((p) => p.id === service.plan.id);
      setService({ equipment: res.data, plan: freshPlan || service.plan });
      setDoneNote("");
      load();
    } catch {
      toast.error("Failed to record");
    } finally {
      setBusy(false);
    }
  };

  const undoCompletion = async (h) => {
    if (!window.confirm(`Undo this completion of "${h.plan.title}" on ${h.equipment.name}?`)) return;
    try {
      await axios.post(`${API}/maintenance/equipment/${h.equipment.id}/plans/${h.plan.id}/undo`, { at: h.at });
      toast.success("Completion undone");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Undo failed");
    }
  };

  const updatePlan = (i, patch) => {
    const plans = [...form.plans];
    plans[i] = { ...plans[i], ...patch };
    setForm({ ...form, plans });
  };

  return (
    <div className="space-y-6" data-testid="maintenance-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-brand-navy tracking-tight">Equipment Maintenance</h2>
          <p className="text-sm text-muted-foreground mt-1">Each machine can have several plans — e.g. filter every 15 days, bearings every 90 days.</p>
        </div>
        <Button onClick={openAdd} className="bg-brand-navy text-white" data-testid="add-equipment-btn">
          <Plus className="w-4 h-4 mr-1" /> Add equipment
        </Button>
      </div>

      {/* Legend */}
      {items.length > 0 && (
        <div className="flex items-center gap-4 text-xs text-neutral-600" data-testid="timeline-legend">
          <span className="flex items-center gap-1.5"><span className="w-4 h-3.5 rounded bg-amber-400 inline-block" /> Pending</span>
          <span className="flex items-center gap-1.5"><span className="w-4 h-3.5 rounded bg-amber-400 ring-2 ring-red-500 inline-block" /> Overdue</span>
          <span className="flex items-center gap-1.5"><span className="w-4 h-3.5 rounded bg-emerald-500 inline-block" /> Completed</span>
          <span className="text-muted-foreground hidden sm:inline">· scroll the calendar left / right</span>
        </div>
      )}

      {loading ? (
        <div className="py-12 text-center text-muted-foreground flex items-center justify-center gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground border rounded-xl bg-white">
          <Wrench className="w-10 h-10 mx-auto mb-3 text-neutral-300" />
          No equipment yet — click "Add equipment" to register your first machine.
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((e) => (
            <div key={e.id} className="border rounded-xl bg-white p-4" data-testid={`equipment-card-${e.id}`}>
              <div className="flex items-start gap-3">
                {e.image_id ? (
                  <img src={imgUrl(e.image_id)} alt="" className="w-12 h-12 object-cover rounded-lg border shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-lg border bg-neutral-50 flex items-center justify-center shrink-0"><Wrench className="w-5 h-5 text-neutral-300" /></div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{e.name}{e.overdue && <span className="ml-2 text-[10px] font-bold text-red-700 bg-red-50 border border-red-300 rounded px-1 py-0.5 align-middle">OVERDUE</span>}</p>
                  {e.location && <p className="text-xs text-muted-foreground truncate">{e.location}</p>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => openEdit(e)} data-testid={`edit-equipment-${e.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-red-600 border-red-200 hover:bg-red-50" onClick={() => remove(e)} data-testid={`delete-equipment-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              <EquipmentTimeline
                equipment={e}
                days={days}
                todayKey={todayKey}
                onSelect={(p) => { setDoneNote(""); setService({ equipment: e, plan: p }); }}
                onSelectDone={(p, rec) => setDoneInfo({ equipment: e, plan: p, rec })}
              />
            </div>
          ))}
        </div>
      )}

      {/* Water quality */}
      <WaterQualityCard />

      {/* Completed — collapsed by default */}
      {completedLog.length > 0 && (
        <div className="border rounded-xl bg-white" data-testid="completed-section">
          <button
            onClick={() => setCompletedOpen(!completedOpen)}
            className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-neutral-50 rounded-xl"
            data-testid="completed-toggle"
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-neutral-700">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              Completed maintenance
              <span className="text-xs font-normal text-muted-foreground">({completedLog.length})</span>
            </span>
            {completedOpen ? <ChevronUp className="w-4 h-4 text-neutral-400" /> : <ChevronDown className="w-4 h-4 text-neutral-400" />}
          </button>
          {completedOpen && (
            <div className="px-4 pb-4 space-y-1.5 max-h-[300px] overflow-y-auto">
              {completedLog.slice(0, 100).map((h, i) => (
                <div key={i} className="flex items-center justify-between gap-3 border rounded-lg px-3 py-1.5 bg-emerald-50/40 border-emerald-100" data-testid={`completed-row-${i}`}>
                  <div className="min-w-0">
                    <p className="text-sm truncate">
                      <span className="font-semibold">{h.equipment.name}</span>
                      <span className="text-muted-foreground"> · {h.plan.title}</span>
                    </p>
                    {h.note && <p className="text-xs text-neutral-500 truncate">{h.note}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <p className="text-xs text-neutral-500 whitespace-nowrap">
                      {new Date(h.at).toLocaleDateString("en-AU", { day: "2-digit", month: "short" })} · {h.by}
                    </p>
                    <button
                      onClick={() => undoCompletion(h)}
                      className="text-[11px] font-semibold text-red-600 border border-red-200 rounded px-1.5 py-0.5 hover:bg-red-50"
                      data-testid={`undo-completion-${i}`}
                    >
                      Undo
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add / edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl" data-testid="equipment-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? "Edit equipment" : "Add equipment"}</DialogTitle>
            <DialogDescription>One machine can have several plans with different intervals.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-3 items-start">
              <ImagePicker imageId={form.image_id} onChange={(id) => setForm({ ...form, image_id: id })} testId="equipment-photo-btn" />
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Name *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Washer #1 — Speed Queen" data-testid="equipment-name-input" />
                </div>
                <div>
                  <Label className="text-xs">Location</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Warehouse bay 2" data-testid="equipment-location-input" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">Notes</Label>
                  <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="equipment-notes-input" />
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs font-semibold uppercase tracking-wider">Maintenance plans</Label>
                <Button type="button" size="sm" variant="outline" className="h-7 text-xs" data-testid="add-plan-btn"
                  onClick={() => setForm({ ...form, plans: [...form.plans, EMPTY_PLAN()] })}>
                  <Plus className="w-3 h-3 mr-1" /> Add plan
                </Button>
              </div>
              <div className="space-y-3 max-h-[42vh] overflow-y-auto pr-1">
                {form.plans.map((p, pi) => (
                  <div key={pi} className="border rounded-xl p-3 bg-neutral-50/60 space-y-2" data-testid={`plan-editor-${pi}`}>
                    <div className="flex gap-2 items-end flex-wrap">
                      <div className="flex-1 min-w-[160px]">
                        <Label className="text-xs">Plan title *</Label>
                        <Input value={p.title} className="bg-white" placeholder="e.g. Clean lint filter" data-testid={`plan-title-${pi}`}
                          onChange={(e) => updatePlan(pi, { title: e.target.value })} />
                      </div>
                      <div className="w-28">
                        <Label className="text-xs">Every (days) *</Label>
                        <Input type="number" min="1" className="bg-white" value={p.interval_days} data-testid={`plan-interval-${pi}`}
                          onChange={(e) => updatePlan(pi, { interval_days: e.target.value })} />
                      </div>
                      <div className="w-40">
                        <Label className="text-xs">First due date</Label>
                        <Input type="date" className="bg-white" value={p.start_date || ""} data-testid={`plan-start-date-${pi}`}
                          onChange={(e) => updatePlan(pi, { start_date: e.target.value })} />
                      </div>
                      <button type="button" className="text-red-500 hover:text-red-700 mb-2" data-testid={`remove-plan-${pi}`}
                        onClick={() => setForm({ ...form, plans: form.plans.filter((_, j) => j !== pi) })}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-1.5">
                      {p.steps.map((s, si) => (
                        <div key={si} className="flex gap-2 items-start">
                          <span className="text-xs font-bold text-neutral-400 mt-2.5 w-4 text-right shrink-0">{si + 1}.</span>
                          <Textarea rows={1} value={s.text} placeholder="Step description…" className="flex-1 bg-white min-h-[36px]"
                            data-testid={`plan-${pi}-step-text-${si}`}
                            onChange={(e) => {
                              const steps = [...p.steps];
                              steps[si] = { ...steps[si], text: e.target.value };
                              updatePlan(pi, { steps });
                            }} />
                          <ImagePicker imageId={s.image_id} testId={`plan-${pi}-step-photo-${si}`}
                            onChange={(id) => {
                              const steps = [...p.steps];
                              steps[si] = { ...steps[si], image_id: id };
                              updatePlan(pi, { steps });
                            }} />
                          <button type="button" className="text-red-400 hover:text-red-600 mt-2.5" data-testid={`plan-${pi}-remove-step-${si}`}
                            onClick={() => updatePlan(pi, { steps: p.steps.filter((_, j) => j !== si) })}>
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <Button type="button" size="sm" variant="ghost" className="h-7 text-xs text-brand-navy" data-testid={`plan-${pi}-add-step`}
                        onClick={() => updatePlan(pi, { steps: [...p.steps, { text: "", image_id: null }] })}>
                        <Plus className="w-3 h-3 mr-1" /> Add step
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-brand-navy text-white" data-testid="save-equipment-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Completion details popup (green block) */}
      <Dialog open={!!doneInfo} onOpenChange={(o) => !o && setDoneInfo(null)}>
        <DialogContent className="max-w-sm" data-testid="done-info-dialog">
          {doneInfo && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-emerald-700">
                  <CheckCircle2 className="w-4 h-4" /> Completed
                </DialogTitle>
                <DialogDescription>{doneInfo.equipment.name} — {doneInfo.plan.title}</DialogDescription>
              </DialogHeader>
              <div className="space-y-1.5 text-sm">
                {doneInfo.rec.due && <p><span className="text-muted-foreground">Scheduled:</span> {new Date(`${doneInfo.rec.due}T00:00:00`).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" })}</p>}
                <p><span className="text-muted-foreground">Done on:</span> {new Date(doneInfo.rec.at).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" })}</p>
                {doneInfo.rec.by && <p><span className="text-muted-foreground">By:</span> {doneInfo.rec.by}</p>}
                {doneInfo.rec.note ? (
                  <div className="border rounded-lg bg-emerald-50/60 border-emerald-200 px-3 py-2" data-testid="done-info-note">
                    <p className="text-xs font-semibold text-emerald-700 mb-0.5">Note</p>
                    <p className="whitespace-pre-wrap">{doneInfo.rec.note}</p>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">No note.</p>
                )}
              </div>
              <DialogFooter className="flex-row justify-between sm:justify-between">
                <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="done-info-undo-btn"
                  onClick={async () => { await undoCompletion({ equipment: doneInfo.equipment, plan: doneInfo.plan, at: doneInfo.rec.at }); setDoneInfo(null); }}>
                  Undo
                </Button>
                <Button size="sm" onClick={() => setDoneInfo(null)} data-testid="done-info-close-btn">Close</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Service dialog (one plan) */}
      <Dialog open={!!service} onOpenChange={(o) => !o && setService(null)}>
        <DialogContent className="max-w-lg" data-testid="service-dialog">
          {service && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Wrench className="w-4 h-4" /> {service.equipment.name} — {service.plan.title}</DialogTitle>
                <DialogDescription>
                  Every {service.plan.interval_days} days
                  {service.plan.last_done_at ? ` · last done ${new Date(service.plan.last_done_at).toLocaleDateString()}` : " · never done yet"}
                  {service.plan.next_due ? ` · next due ${service.plan.next_due}` : ""}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                {(service.plan.steps || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No steps documented.</p>
                ) : (
                  <ol className="space-y-3">
                    {service.plan.steps.map((s, i) => (
                      <li key={i} className="flex gap-3 items-start" data-testid={`service-step-${i}`}>
                        <span className="w-6 h-6 rounded-full bg-brand-navy text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                        <div className="min-w-0">
                          <p className="text-sm whitespace-pre-wrap">{s.text}</p>
                          {s.image_id && <img src={imgUrl(s.image_id)} alt="" className="mt-1.5 rounded-lg border max-h-44 object-contain" />}
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
                <div className="border-t pt-3 space-y-2">
                  <Label className="text-xs">Completion note (optional)</Label>
                  <Input value={doneNote} onChange={(e) => setDoneNote(e.target.value)} placeholder="e.g. replaced belt, greased bearings" data-testid="done-note-input" />
                  <Button onClick={markDone} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mark-done-btn">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark maintenance done</>}
                  </Button>
                </div>
                {(service.plan.history || []).length > 0 && (
                  <div className="border-t pt-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-1"><History className="w-3 h-3" /> History</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {service.plan.history.map((h, i) => (
                        <p key={i} className="text-xs text-neutral-600">
                          {new Date(h.at).toLocaleString()} — {h.by}{h.note ? ` · ${h.note}` : ""}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
